import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Plus, Trash2, Paperclip, Camera, X, ChevronLeft, ChevronDown, ChevronUp } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { ADMIN_ROLE } from "@/constants/roles";
import { UOM_OPTIONS } from "@/constants/uom";
import { PAYMENT_METHODS, expenseStatus, expenseIsOpen } from "@/constants/outsidePurchases";
import DocumentsPanel, { ACCEPT, uploadDocuments } from "@/components/DocumentsPanel";
import QboVendorPicker from "@/components/QboVendorPicker";
import SearchableSelect from "@/components/SearchableSelect";
import JobBudgetPanel from "@/components/JobBudgetPanel";
import JobDeliveryAddress from "@/components/JobDeliveryAddress";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
const emptyLine = () => ({ description: "", qty: "", uom: "each", unit_price: "", amount: "", cost_code_id: "", billability: "non_billable", taxable: false });
const num = (v) => (v === "" || v == null ? null : Number(v));

const errorText = (e, fallback) => {
  const detail = e.response?.data?.detail;
  return Array.isArray(detail) ? detail.map((d) => d.message || d.msg).join(" ") : detail || fallback;
};

const Field = ({ label, required, hint, children }) => (
  <div>
    <label className="text-[13px] font-medium text-slate-700">{label}{required && " *"}</label>
    <div className="mt-1.5">{children}</div>
    {hint && <p className="text-[11.5px] text-slate-400 mt-1">{hint}</p>}
  </div>
);
const inputCls = "w-full h-10 px-3 rounded-lg border border-slate-200 text-sm outline-none focus:border-sky-400";

// Meeting 3 — one Expense Log form, laid out like the client's Smartsheet:
// purchaser, date, vendor, vendor invoice #, job, quoted/actual amount,
// card or AP, cost account, memo, attachments. Line items are optional
// (under "More details") for an entry split across cost codes.
export default function ExpenseLogForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === ADMIN_ROLE;

  const [jobs, setJobs] = useState([]);
  const [contexts, setContexts] = useState([]);
  const [changeOrders, setChangeOrders] = useState([]);
  const [costCodes, setCostCodes] = useState([]);
  const [team, setTeam] = useState([]);
  const [localVendors, setLocalVendors] = useState([]);
  const [qboConnected, setQboConnected] = useState(false);
  const [qboVendor, setQboVendor] = useState(null); // {qbo_id, name}
  const [loaded, setLoaded] = useState(!isEdit);

  const [purchasedBy, setPurchasedBy] = useState("");
  const [purchaseDate, setPurchaseDate] = useState(new Date().toISOString().slice(0, 10));
  const [vendorMode, setVendorMode] = useState("existing");
  const [vendorId, setVendorId] = useState("");
  const [newVendorName, setNewVendorName] = useState("");
  const [legacyVendorFreetext, setLegacyVendorFreetext] = useState("");
  const [vendorInvoiceNumber, setVendorInvoiceNumber] = useState("");
  const [contextType, setContextType] = useState("job");
  const [jobId, setJobId] = useState("");
  const [coJobId, setCoJobId] = useState("");
  const [changeOrderId, setChangeOrderId] = useState("");
  const [nonJobContextId, setNonJobContextId] = useState("");
  const [quotedAmount, setQuotedAmount] = useState("");
  const [actualAmount, setActualAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [cardLast4, setCardLast4] = useState("");
  const [costCodeId, setCostCodeId] = useState("");
  const [memo, setMemo] = useState("");
  const [taxable, setTaxable] = useState(false);
  const [adminApprovalNote, setAdminApprovalNote] = useState("");

  const [showMore, setShowMore] = useState(false);
  const [itemized, setItemized] = useState(false);
  const [lines, setLines] = useState([emptyLine()]);
  // Delivery address: the job's own address(es), the company default
  // (and office/warehouse), or a new one typed right here.
  const [jobAddresses, setJobAddresses] = useState([]);
  const [companyAddresses, setCompanyAddresses] = useState([]);
  const [addressHint, setAddressHint] = useState(null);
  const [deliveryAddressId, setDeliveryAddressId] = useState("");
  const [addressFixed, setAddressFixed] = useState(false);
  const [newAddress, setNewAddress] = useState(null); // {address_line1, city, state, zip} while adding
  const [savingAddress, setSavingAddress] = useState(false);

  const [status, setStatus] = useState("draft");
  const [poNumber, setPoNumber] = useState(null);
  const [approvalMessage, setApprovalMessage] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  useEffect(() => {
    api.get("/contractor/jobs").then((r) => setJobs(r.data));
    api.get("/contractor/contexts").then((r) => setContexts(r.data));
    api.get("/contractor/cost-codes").then((r) => setCostCodes(r.data));
    api.get("/contractor/local-vendors").then((r) => setLocalVendors(r.data));
    // With QuickBooks connected the vendor list IS QuickBooks' (Package 42);
    // otherwise the out-of-network vendor records are the fallback.
    api.get("/contractor/qbo-vendors", { params: { q: "" } }).then((r) => setQboConnected(r.data.connected)).catch(() => {});
    if (isAdmin) api.get("/contractor/team").then((r) => setTeam(r.data.filter((m) => m.status === "active")));
    if (isEdit) return;
    // Carried over from Quick Log: inherit the job already pinned on the
    // cart session as a prefill.
    api.get("/contractor/cart/context").then((r) => {
      const ctx = r.data.context;
      if (!ctx) return;
      setContextType(ctx.type);
      if (ctx.type === "job") setJobId(ctx.job_id);
      else if (ctx.type === "change_order") { setCoJobId(ctx.job_id); setChangeOrderId(ctx.change_order_id); }
      else if (ctx.type === "non_job") setNonJobContextId(ctx.context_id);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const effectiveJobId = contextType === "job" ? jobId : contextType === "change_order" ? coJobId : null;
  const loadAddressOptions = (selectId) => api.get("/contractor/expense-delivery-options", {
    params: { ...(effectiveJobId ? { job_id: effectiveJobId } : {}), ...((selectId || deliveryAddressId) ? { selected_id: selectId || deliveryAddressId } : {}) },
  }).then((r) => {
    setJobAddresses(r.data.job);
    setCompanyAddresses(r.data.company);
    setAddressHint(r.data.hint);
    setAddressFixed(!!r.data.fixed);
    if (r.data.fixed) setDeliveryAddressId(r.data.preselect_id);
    else if (selectId) setDeliveryAddressId(selectId);
    else if (!deliveryAddressId && r.data.preselect_id) setDeliveryAddressId(r.data.preselect_id);
  }).catch(() => {});

  useEffect(() => {
    loadAddressOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextType, jobId, coJobId]);

  // An entry being edited may point at an address that isn't one of the
  // standard choices — fetch again so it shows instead of a blank picker.
  useEffect(() => {
    if (deliveryAddressId && ![...jobAddresses, ...companyAddresses].some((a) => a.id === deliveryAddressId)) loadAddressOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryAddressId]);

  const saveNewAddress = async () => {
    if (!newAddress?.address_line1?.trim()) { toast.error("Enter the street address"); return; }
    setSavingAddress(true);
    try {
      const { data } = await api.post("/contractor/delivery-addresses/typed", { ...newAddress, job_id: effectiveJobId || null });
      toast.success(effectiveJobId ? "Address added — it's saved on this job for next time" : "Address added");
      setNewAddress(null);
      await loadAddressOptions(data.id);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map((x) => x.msg || x.message).join(" ") : d || "Couldn't add the address");
    } finally {
      setSavingAddress(false);
    }
  };

  useEffect(() => {
    if (contextType !== "change_order" || !coJobId) { setChangeOrders([]); return; }
    api.get(`/contractor/jobs/${coJobId}/change-orders`).then((r) => setChangeOrders(r.data));
  }, [contextType, coJobId]);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/contractor/outside-purchases/${id}`).then((r) => {
      const op = r.data;
      if (!expenseIsOpen(op)) { navigate(`/contractor/expense-log/${id}`); return; }
      setStatus(expenseStatus(op));
      setPoNumber(op.po_number);
      setApprovalMessage(["needs_info", "rejected"].includes(op.approval_status) ? op.approval_message : null);
      setPurchasedBy(op.purchased_by || "");
      setPurchaseDate(op.purchase_date || new Date().toISOString().slice(0, 10));
      if (op.qbo_vendor_ref) setQboVendor({ qbo_id: op.qbo_vendor_ref, name: op.vendor_name_freetext });
      else if (op.vendor_id) { setVendorMode("existing"); setVendorId(op.vendor_id); }
      else if (op.vendor_name_freetext) { setLegacyVendorFreetext(op.vendor_name_freetext); setVendorMode("new"); setNewVendorName(op.vendor_name_freetext); }
      setVendorInvoiceNumber(op.vendor_invoice_number || "");
      const ctx = op.context || {};
      setContextType(ctx.type || "job");
      if (ctx.type === "job") setJobId(ctx.job_id || "");
      if (ctx.type === "change_order") { setCoJobId(ctx.job_id || ""); setChangeOrderId(ctx.change_order_id || ""); }
      if (ctx.type === "non_job") setNonJobContextId(ctx.context_id || "");
      setQuotedAmount(op.quoted_amount ?? "");
      setPaymentMethod(op.payment_method || "");
      setCardLast4(op.card_last4 || "");
      setMemo(op.memo || "");
      setTaxable(!!op.taxable);
      setAdminApprovalNote(op.admin_approval_note || "");
      setDeliveryAddressId(op.delivery_address_id || "");
      const opLines = (op.lines || []).filter((l) => !l.is_tax);
      if (opLines.length > 1) {
        setItemized(true);
        setShowMore(true);
        setLines(opLines.map((l) => ({
          description: l.description || "", qty: l.qty ?? "", uom: l.uom || "each", unit_price: l.unit_price ?? "",
          amount: l.amount ?? "", cost_code_id: l.cost_code_id || "", billability: l.billability || "non_billable", taxable: !!l.taxable,
        })));
        setCostCodeId(op.cost_code_id || "");
      } else {
        setActualAmount(opLines[0]?.amount ?? (op.total || ""));
        setTaxable(!!(opLines[0]?.taxable ?? op.taxable));
        setCostCodeId(op.cost_code_id || opLines[0]?.cost_code_id || "");
      }
      setLoaded(true);
    }).catch(() => {
      toast.error("Could not load this expense");
      navigate("/contractor/expense-log");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const updateLine = (i, field, value) => setLines(lines.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)));
  const lineAmount = (l) => (l.amount !== "" && l.amount != null ? Number(l.amount) : (Number(l.qty) || 0) * (Number(l.unit_price) || 0));
  const itemizedTotal = lines.reduce((s, l) => s + lineAmount(l), 0);
  const isCard = ["company_card", "personal_card", "ramp"].includes(paymentMethod);
  const selectedJob = jobs.find((j) => j.id === (contextType === "job" ? jobId : contextType === "change_order" ? coJobId : null));
  const preTax = itemized ? itemizedTotal : Number(actualAmount) || 0;
  // Tax applies only to lines marked taxable (a one-line entry's Taxable box marks that line).
  const taxableBase = itemized ? lines.filter((l) => l.taxable).reduce((s, l) => s + lineAmount(l), 0) : taxable ? preTax : 0;
  const anyTaxable = itemized ? lines.some((l) => l.taxable) : taxable;
  const taxPreview = anyTaxable && selectedJob?.tax_rate ? Math.round(taxableBase * selectedJob.tax_rate) / 100 : null;

  const buildContext = () => {
    if (contextType === "job") return jobId ? { type: "job", job_id: jobId } : null;
    if (contextType === "change_order") return changeOrderId ? { type: "change_order", change_order_id: changeOrderId } : null;
    return nonJobContextId ? { type: "non_job", context_id: nonJobContextId } : null;
  };

  const buildPayload = (submit) => {
    const vendorFields = qboVendor
      ? { qbo_vendor_id: qboVendor.qbo_id }
      : vendorMode === "existing"
        ? { vendor_id: vendorId || null }
        : { new_vendor: newVendorName.trim() ? { name: newVendorName.trim() } : null };
    const payload = {
      ...vendorFields, purchase_date: purchaseDate, context: buildContext() || undefined,
      vendor_invoice_number: vendorInvoiceNumber, quoted_amount: num(quotedAmount),
      payment_method: paymentMethod || null, card_last4: isCard ? cardLast4 : "",
      cost_code_id: costCodeId, memo, admin_approval_note: adminApprovalNote, ...(itemized ? {} : { taxable }),
      delivery_address_id: deliveryAddressId || null, submit,
    };
    if (isAdmin && purchasedBy) payload.purchased_by = purchasedBy;
    if (itemized) {
      payload.lines = lines.filter((l) => l.description || l.amount !== "").map((l) => ({
        description: l.description || memo || "Expense", qty: num(l.qty), uom: l.uom, unit_price: num(l.unit_price),
        amount: num(l.amount), cost_code_id: l.cost_code_id || null, billability: l.billability, taxable: !!l.taxable,
      }));
    } else {
      payload.actual_amount = num(actualAmount);
    }
    return payload;
  };

  const addPendingFiles = (list) => {
    const picked = Array.from(list || []);
    if (picked.length) setPendingFiles((prev) => [...prev, ...picked]);
  };

  // A new entry is saved as a draft first, then its files upload, then it's
  // submitted. If the submit is refused, the user lands on the saved
  // draft's edit page, so a second click edits it instead of creating
  // another one (Meeting 3 duplicate-drafts bug).
  const save = async (submit) => {
    const useQbo = qboConnected || qboVendor;
    if (useQbo && !qboVendor) return toast.error("Pick a vendor from QuickBooks, or add a new one");
    if (!useQbo && vendorMode === "existing" && !vendorId) return toast.error("Select a vendor");
    if (!useQbo && vendorMode === "new" && !newVendorName.trim()) return toast.error("Enter the vendor's name");
    setSaving(true);
    let savedId = id;
    try {
      if (!isEdit) {
        const { data } = await api.post("/contractor/outside-purchases", buildPayload(false));
        savedId = data.id;
        if (pendingFiles.length) {
          const failed = await uploadDocuments("outside_purchase", savedId, pendingFiles, "receipt");
          failed.forEach((f) => toast.error(`${f.name}: ${f.message}`));
          setPendingFiles([]);
        }
        if (submit) await api.patch(`/contractor/outside-purchases/${savedId}`, { submit: true });
      } else {
        await api.patch(`/contractor/outside-purchases/${id}`, buildPayload(submit));
      }
      toast.success(submit ? (status === "draft" ? "Expense submitted for approval" : "Expense resubmitted for approval") : "Saved");
      navigate(`/contractor/expense-log/${savedId}`);
    } catch (e) {
      toast.error(errorText(e, "Failed to save"));
      if (!isEdit && savedId) navigate(`/contractor/expense-log/${savedId}/edit`, { replace: true });
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <div className="p-8 text-slate-400 text-sm">Loading…</div>;

  return (
    <div className="p-6 md:p-8 max-w-3xl" data-testid="expense-form-page">
      <Link to="/contractor/expense-log" className="text-[12.5px] text-slate-500 flex items-center gap-1 mb-3"><ChevronLeft size={13} /> Back to Expense Log</Link>
      <h1 className="text-2xl font-extrabold tracking-tight">Expense Log</h1>
      <p className="text-slate-500 text-sm mt-1">
        {isEdit ? <>Modify {poNumber ? <span className="mono font-semibold text-slate-700">{poNumber}</span> : "this entry"}.</> : "New entry"} — every purchase made outside a PO gets logged here.
      </p>
      {approvalMessage && (
        <div className="mt-4 text-[12.5px] text-amber-900 bg-amber-50 border border-amber-100 rounded-lg px-3.5 py-3" data-testid="expense-needs-info-banner">
          <div className="font-semibold text-[11px] uppercase tracking-wide text-amber-600 mb-0.5">{status === "rejected" ? "Rejected — reason" : "More info requested"}</div>
          {approvalMessage}
          <div className="mt-1 text-amber-700">Update the entry below, then click Resubmit.</div>
        </div>
      )}

      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <Field label="Purchaser">
          {isAdmin ? (
            <SearchableSelect value={purchasedBy || user?.id} onChange={(e) => setPurchasedBy(e.target.value)} data-testid="expense-purchaser-select" className={inputCls}>
              {team.length === 0 && <option value={user?.id}>{user?.name}</option>}
              {team.map((m) => <option key={m.id} value={m.id}>{m.name}{m.id === user?.id ? " (you)" : ""}</option>)}
            </SearchableSelect>
          ) : (
            <div className="h-10 px-3 rounded-lg border border-slate-100 bg-slate-50 text-sm flex items-center">{user?.name}</div>
          )}
        </Field>
        <Field label="Date">
          <input type="date" value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)} data-testid="expense-date-input" className={inputCls} />
        </Field>
      </div>

      {qboConnected || qboVendor ? (
        <div className="mt-4">
          <Field label="Vendor" required>
            <QboVendorPicker value={qboVendor} onChange={setQboVendor} />
          </Field>
        </div>
      ) : (
      <div className="mt-4">
        <div className="flex items-center justify-between">
          <label className="text-[13px] font-medium text-slate-700">Vendor *</label>
          <div className="flex gap-1">
            <button type="button" onClick={() => setVendorMode("existing")} data-testid="expense-vendor-mode-existing" className={`px-2.5 h-7 rounded-md text-[11.5px] font-semibold ${vendorMode === "existing" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>Pick</button>
            <button type="button" onClick={() => setVendorMode("new")} data-testid="expense-vendor-mode-new" className={`px-2.5 h-7 rounded-md text-[11.5px] font-semibold ${vendorMode === "new" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>Add new</button>
          </div>
        </div>
        {legacyVendorFreetext && <p className="text-[11.5px] text-amber-600 mt-1">Logged as "{legacyVendorFreetext}". Pick or add a proper vendor record to replace it.</p>}
        <div className="mt-1.5">
          {vendorMode === "existing" ? (
            <SearchableSelect value={vendorId} onChange={(e) => setVendorId(e.target.value)} data-testid="expense-vendor-select" className={inputCls}>
              <option value="">Select vendor…</option>
              {localVendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </SearchableSelect>
          ) : (
            <input value={newVendorName} onChange={(e) => setNewVendorName(e.target.value)} placeholder="Vendor name" data-testid="expense-new-vendor-input" className={inputCls} />
          )}
        </div>
        <p className="text-[11.5px] text-slate-400 mt-1">Connect QuickBooks to pick vendors from your QuickBooks vendor list.</p>
      </div>
      )}

      <div className="mt-4 grid sm:grid-cols-2 gap-4">
        <Field label="Vendor invoice #">
          <input value={vendorInvoiceNumber} onChange={(e) => setVendorInvoiceNumber(e.target.value)} placeholder="From the receipt or invoice" data-testid="expense-vendor-invoice-input" className={inputCls} />
        </Field>
        <Field label="Job" required>
          <div className="flex gap-1 mb-1.5">
            {[["job", "Job"], ["change_order", "Change order"], ["non_job", "Non-job"]].map(([t, label]) => (
              <button key={t} type="button" onClick={() => { setContextType(t); setJobId(""); setCoJobId(""); setChangeOrderId(""); setNonJobContextId(""); }} data-testid={`expense-context-type-${t}`} className={`px-2.5 h-7 rounded-md text-[11.5px] font-semibold ${contextType === t ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>{label}</button>
            ))}
          </div>
          {contextType === "job" && (
            <SearchableSelect value={jobId} onChange={(e) => setJobId(e.target.value)} data-testid="expense-job-select" className={inputCls}>
              <option value="">Select job…</option>
              {jobs.map((j) => <option key={j.id} value={j.id}>{j.code} — {j.name}</option>)}
            </SearchableSelect>
          )}
          {contextType === "change_order" && (
            <div className="grid grid-cols-2 gap-2">
              <SearchableSelect value={coJobId} onChange={(e) => { setCoJobId(e.target.value); setChangeOrderId(""); }} className={inputCls}>
                <option value="">Job…</option>
                {jobs.map((j) => <option key={j.id} value={j.id}>{j.code} — {j.name}</option>)}
              </SearchableSelect>
              <SearchableSelect value={changeOrderId} onChange={(e) => setChangeOrderId(e.target.value)} disabled={!coJobId} className={`${inputCls} disabled:opacity-50`}>
                <option value="">Change order…</option>
                {changeOrders.map((co) => <option key={co.id} value={co.id}>{co.code} — {co.name}</option>)}
              </SearchableSelect>
            </div>
          )}
          {contextType === "non_job" && (
            <SearchableSelect value={nonJobContextId} onChange={(e) => setNonJobContextId(e.target.value)} className={inputCls}>
              <option value="">Select…</option>
              {contexts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </SearchableSelect>
          )}
        </Field>
      </div>
      {selectedJob && <JobBudgetPanel jobId={selectedJob.id} testId="expense-budget-panel" />}

      <div className="mt-4 grid sm:grid-cols-2 gap-4">
        <Field label="Quoted amount">
          <input type="number" min="0" step="0.01" value={quotedAmount} onChange={(e) => setQuotedAmount(e.target.value)} placeholder="0.00" data-testid="expense-quoted-input" className={inputCls} />
        </Field>
        <Field label="Actual amount" required hint={itemized ? "Total of the line items below." : null}>
          {itemized ? (
            <div className="h-10 px-3 rounded-lg border border-slate-100 bg-slate-50 text-sm flex items-center mono">{fmt(itemizedTotal)}</div>
          ) : (
            <input type="number" min="0" step="0.01" value={actualAmount} onChange={(e) => setActualAmount(e.target.value)} placeholder="0.00" data-testid="expense-actual-input" className={inputCls} />
          )}
        </Field>
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-4">
        <Field label="Card or Accounts Payable" required>
          <SearchableSelect value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} data-testid="expense-payment-select" className={inputCls}>
            <option value="">Select…</option>
            {PAYMENT_METHODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </SearchableSelect>
        </Field>
        <Field label="Cost account" required hint="Your QuickBooks chart of accounts, through your cost codes.">
          <SearchableSelect value={costCodeId} onChange={(e) => setCostCodeId(e.target.value)} data-testid="expense-cost-account-select" className={inputCls}>
            <option value="">Select…</option>
            {costCodes.map((cc) => <option key={cc.id} value={cc.id}>{cc.code} — {cc.name}</option>)}
          </SearchableSelect>
        </Field>
      </div>

      <div className="mt-3" data-testid="expense-tax-row">
        {itemized ? (
          <p className="text-[12px] text-slate-500">Mark each taxable line below.</p>
        ) : (
        <label className="flex items-center gap-2 text-[13px] text-slate-700">
          <input type="checkbox" checked={taxable} onChange={(e) => setTaxable(e.target.checked)} data-testid="expense-taxable-checkbox" />
          Taxable
        </label>
        )}
        {anyTaxable && (
          <p className="text-[12px] mt-1 ml-6" data-testid="expense-tax-preview">
            {!selectedJob ? <span className="text-slate-400">Pick a job — tax uses the job's rate.</span>
              : selectedJob.tax_rate ? <span className="text-slate-600">Tax at {selectedJob.tax_rate}% ({selectedJob.code}): <b className="mono">{fmt(taxPreview)}</b> · Total <b className="mono">{fmt(preTax + (taxPreview || 0))}</b></span>
              : <span className="text-amber-700">{selectedJob.code} has no tax rate yet — an admin sets it on the job page.</span>}
          </p>
        )}
      </div>

      {isCard && (
        <div className="mt-4 sm:w-1/2 sm:pr-2">
          <Field label="Card last 4">
            <input value={cardLast4} onChange={(e) => setCardLast4(e.target.value.replace(/\D/g, ""))} maxLength={4} placeholder="1234" data-testid="expense-card-last4-input" className={inputCls} />
          </Field>
        </div>
      )}
      {paymentMethod === "cash" && (
        <div className="mt-4">
          <Field label="Admin approval note" hint={'Only if there\'s no receipt — explain why (e.g. "receipt lost, confirmed with vendor").'}>
            <textarea value={adminApprovalNote} onChange={(e) => setAdminApprovalNote(e.target.value)} rows={2} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
          </Field>
        </div>
      )}

      <div className="mt-4">
        <Field label="Memo">
          <textarea value={memo} onChange={(e) => setMemo(e.target.value)} rows={2} placeholder="What was bought and why" data-testid="expense-memo-input" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-sky-400" />
        </Field>
      </div>

      <div className="mt-6">
        <label className="text-[13px] font-semibold text-slate-700">Attachments *</label>
        {isEdit ? (
          <DocumentsPanel entityType="outside_purchase" entityId={id} title="" compact defaultDocType="receipt" />
        ) : (
          <div className="mt-2">
            <div className="flex gap-2">
              <button type="button" onClick={() => fileInputRef.current?.click()} data-testid="expense-attach-button" className="flex-1 sm:flex-none flex items-center justify-center gap-2 h-11 sm:h-9 px-4 rounded-lg border border-slate-200 text-[13px] font-semibold hover:bg-slate-50">
                <Paperclip size={14} /> Attach receipt or invoice
              </button>
              <button type="button" onClick={() => cameraInputRef.current?.click()} data-testid="expense-camera-button" className="sm:hidden flex-1 flex items-center justify-center gap-2 h-11 rounded-lg border-2 border-sky-200 bg-sky-50 text-sky-700 text-[13px] font-semibold">
                <Camera size={16} /> Take Photo
              </button>
            </div>
            <input ref={fileInputRef} type="file" accept={ACCEPT} multiple className="hidden" data-testid="expense-file-input" onChange={(e) => { addPendingFiles(e.target.files); e.target.value = ""; }} />
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { addPendingFiles(e.target.files); e.target.value = ""; }} />
            {pendingFiles.length > 0 && (
              <div className="mt-2 space-y-1.5" data-testid="expense-pending-files">
                {pendingFiles.map((f, i) => (
                  <div key={`${f.name}-${i}`} className="flex items-center gap-2 text-[12.5px] border border-slate-200 rounded-lg px-3 py-2">
                    <Paperclip size={12} className="text-slate-400 shrink-0" />
                    <span className="truncate flex-1">{f.name}</span>
                    <button type="button" onClick={() => setPendingFiles(pendingFiles.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-500" title="Remove"><X size={13} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <button type="button" onClick={() => setShowMore(!showMore)} data-testid="expense-more-toggle" className="mt-6 flex items-center gap-1 text-[12.5px] font-semibold text-slate-500 hover:text-slate-700">
        {showMore ? <ChevronUp size={14} /> : <ChevronDown size={14} />} More details (delivery address, split across cost codes)
      </button>
      {showMore && (
        <div className="mt-3 space-y-4 border-l-2 border-slate-100 pl-4">
          {addressFixed ? (
            <Field label="Delivery address">
              <JobDeliveryAddress address={jobAddresses[0]} jobId={effectiveJobId} testId="expense-job-delivery-address" />
            </Field>
          ) : (
          <Field label="Delivery address" hint={addressHint}>
            <SearchableSelect
              value={newAddress ? "__new__" : deliveryAddressId}
              onChange={(e) => {
                if (e.target.value === "__new__") { setNewAddress({ address_line1: "", city: "", state: "", zip: "" }); return; }
                setNewAddress(null); setDeliveryAddressId(e.target.value);
              }}
              data-testid="expense-delivery-address-select" className={inputCls}
            >
              <option value="">None</option>
              {jobAddresses.length > 0 && (
                <optgroup label="This job">
                  {jobAddresses.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
                </optgroup>
              )}
              {companyAddresses.length > 0 && (
                <optgroup label="Company">
                  {companyAddresses.map((a) => <option key={a.id} value={a.id}>{a.label}{a.is_default ? " (default)" : a.kind ? ` (${a.kind})` : ""}</option>)}
                </optgroup>
              )}
              <option value="__new__">+ Add a new address…</option>
            </SearchableSelect>
          </Field>
          )}
          {newAddress && (
            <div className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="expense-new-address">
              <input value={newAddress.address_line1} onChange={(e) => setNewAddress({ ...newAddress, address_line1: e.target.value })}
                placeholder="Street address" data-testid="new-address-line1" className={inputCls} />
              <div className="grid grid-cols-3 gap-2">
                <input value={newAddress.city} onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })} placeholder="City" data-testid="new-address-city" className={inputCls} />
                <input value={newAddress.state} onChange={(e) => setNewAddress({ ...newAddress, state: e.target.value })} placeholder="State" data-testid="new-address-state" className={inputCls} />
                <input value={newAddress.zip} onChange={(e) => setNewAddress({ ...newAddress, zip: e.target.value })} placeholder="ZIP" data-testid="new-address-zip" className={inputCls} />
              </div>
              <div className="flex items-center gap-2">
                <button type="button" disabled={savingAddress} onClick={saveNewAddress} data-testid="save-new-address" className="px-3 py-1.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[12.5px] font-semibold disabled:opacity-60">
                  {savingAddress ? "Adding…" : "Add address"}
                </button>
                <button type="button" onClick={() => setNewAddress(null)} className="px-3 py-1.5 rounded-lg border border-slate-200 text-[12.5px] font-semibold">Cancel</button>
                <span className="text-[11.5px] text-slate-400">{effectiveJobId ? "Saved on this job, so it's offered next time." : "Saved to your company's addresses."}</span>
              </div>
            </div>
          )}
          <label className="flex items-center gap-2 text-[13px] text-slate-700">
            <input type="checkbox" checked={itemized} onChange={(e) => setItemized(e.target.checked)} data-testid="expense-itemize-toggle" />
            Split this expense into line items
          </label>
          {itemized && (
            <div className="space-y-3">
              {lines.map((line, i) => (
                <div key={i} className="border border-slate-200 rounded-xl p-3 space-y-2" data-testid={`expense-line-${i}`}>
                  <div className="flex items-center gap-2">
                    <input className="flex-1 h-10 px-2.5 rounded-lg border border-slate-200 text-[13px]" placeholder="Description" value={line.description} onChange={(e) => updateLine(i, "description", e.target.value)} />
                    <button onClick={() => setLines(lines.filter((_, idx) => idx !== i))} className="w-10 h-10 shrink-0 text-slate-400 hover:text-red-500 flex items-center justify-center" title="Remove line"><Trash2 size={15} /></button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <input type="number" min="0" className="h-10 px-2.5 rounded-lg border border-slate-200 text-[13px]" placeholder="Qty" value={line.qty} onChange={(e) => updateLine(i, "qty", e.target.value)} />
                    <SearchableSelect className="h-10 px-2 rounded-lg border border-slate-200 text-[12.5px]" value={line.uom} onChange={(e) => updateLine(i, "uom", e.target.value)}>
                      {UOM_OPTIONS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                    </SearchableSelect>
                    <input type="number" min="0" step="0.01" className="h-10 px-2.5 rounded-lg border border-slate-200 text-[13px]" placeholder="Unit price" value={line.unit_price} onChange={(e) => updateLine(i, "unit_price", e.target.value)} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input type="number" min="0" step="0.01" className="h-10 px-2.5 rounded-lg border border-slate-200 text-[13px]" placeholder="Or amount" value={line.amount} onChange={(e) => updateLine(i, "amount", e.target.value)} />
                    <SearchableSelect className="h-10 px-2 rounded-lg border border-slate-200 text-[12.5px]" value={line.cost_code_id} onChange={(e) => updateLine(i, "cost_code_id", e.target.value)}>
                      <option value="">Same cost account as above</option>
                      {costCodes.map((cc) => <option key={cc.id} value={cc.id}>{cc.code} — {cc.name}</option>)}
                    </SearchableSelect>
                  </div>
                  <div className="flex items-center justify-between text-[12px] text-slate-500">
                    <label className="flex items-center gap-2 text-[12.5px] text-slate-700">
                      <input type="checkbox" checked={!!line.taxable} onChange={(e) => updateLine(i, "taxable", e.target.checked)} data-testid={`expense-line-taxable-${i}`} />
                      Taxable
                    </label>
                    <span>Line amount: {fmt(lineAmount(line))}</span>
                  </div>
                </div>
              ))}
              <button onClick={() => setLines([...lines, emptyLine()])} className="text-[12.5px] font-semibold text-sky-600 flex items-center gap-1"><Plus size={13} /> Add line</button>
            </div>
          )}
        </div>
      )}

      <div className="mt-7 flex flex-col sm:flex-row gap-3">
        {status === "draft" && (
          <>
            <button disabled={saving} onClick={() => save(false)} data-testid="save-expense-draft-button" className="h-11 sm:h-auto px-4 sm:py-2.5 rounded-lg border border-slate-200 text-[13px] font-semibold hover:bg-slate-50 disabled:opacity-60">Save as Draft</button>
            <button disabled={saving} onClick={() => save(true)} data-testid="submit-expense-button" className="h-11 sm:h-auto px-4 sm:py-2.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-semibold disabled:opacity-60">Submit for Approval</button>
          </>
        )}
        {status === "pending_approval" && (
          <button disabled={saving} onClick={() => save(false)} data-testid="save-expense-changes-button" className="h-11 sm:h-auto px-4 sm:py-2.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-semibold disabled:opacity-60">Save Changes</button>
        )}
        {(status === "needs_info" || status === "rejected") && (
          <>
            <button disabled={saving} onClick={() => save(false)} data-testid="save-expense-changes-button" className="h-11 sm:h-auto px-4 sm:py-2.5 rounded-lg border border-slate-200 text-[13px] font-semibold hover:bg-slate-50 disabled:opacity-60">Save</button>
            <button disabled={saving} onClick={() => save(true)} data-testid="resubmit-expense-button" className="h-11 sm:h-auto px-4 sm:py-2.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-semibold disabled:opacity-60">Resubmit</button>
          </>
        )}
      </div>
      {status === "draft" && <p className="mt-2 text-[11.5px] text-slate-400">Submitting needs a job, a cost account, card or accounts payable, the actual amount, and an attached receipt (cash with no receipt can use an admin approval note).</p>}
    </div>
  );
}
